import React, { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { GetRaffleReq, GetRaffleResp, GetRaffleSuccessResp, IRaffle, ParticipateReq, ParticipateResp, RaffleUID } from "./types"
import { useApi } from "@api"
import { usePiano } from "../Piano"
import { ApiError, isApiError } from "@api/types"

export interface RaffleContextType {
  busy: boolean,
  raffle?: IRaffle,
  actionGetRaffle(): void,
  actionParticipate(): void,
  error?: "couldNotFetch" | "couldNotParticipate" | ApiError | { message: string },
  disableParticipateButton?: boolean,
  wasAlreadyParticipatingOnFirstLoad?: boolean,
}
export const RaffleContext = React.createContext<RaffleContextType>({ actionGetRaffle: () => {}, actionParticipate: () => {}, busy: false})

export const useRaffle = () => useContext(RaffleContext)

export const WithConnectedRaffleRaw: React.FC<PropsWithChildren<{raffleUid: RaffleUID}>> = ({ raffleUid, children }) => {
  const { send } = useApi()
  const sendRef = useRef(send)
  sendRef.current = send
  const uidRef = useRef(raffleUid)
  uidRef.current = raffleUid
  const { forceLogin, userInfo, loginCooldown } = usePiano()
  const userInfoRef = useRef(userInfo)
  const forceLoginRef = useRef(forceLogin)
  forceLoginRef.current = forceLogin
  userInfoRef.current = userInfo
  const [ state, setState ] = useState<Omit<RaffleContextType, "actionGetRaffle" | "actionParticipate">>({busy: true})
  const disableParticipateButton = !!loginCooldown

  const actionGetRaffle = useCallback(() => {
    setState((prev) => ({...prev, busy: true, error: undefined}))
    sendRef.current<GetRaffleReq, GetRaffleResp>("raffle/get_raffle", { uid: uidRef.current, user_token: userInfoRef.current?.token ?? undefined })
      .then((resp) => {
        if (isApiError(resp.data)) {
          setState((prev) => ({...prev, busy: false, error: resp.data as ApiError}))
        } else if (!resp.data || resp.error) {
          setState((prev) => ({...prev, busy: false, error: "couldNotFetch" }))          
        } else {
          setState((prev) => {
            const loadedRaffle = (resp.data as GetRaffleSuccessResp).raffle
            const raffleWasLoadedJustNow = !prev.raffle
            
            return {
              ...prev,
              busy: false,
              error: undefined,
              raffle: loadedRaffle,
              wasAlreadyParticipatingOnFirstLoad: raffleWasLoadedJustNow
                ? loadedRaffle.participation?.type === "participating"
                : prev.wasAlreadyParticipatingOnFirstLoad,
            }
          })
        }
      })
      .catch(() => {
        setState((prev) => ({...prev, busy: false, error: "couldNotFetch" }))
      })
  }, [])
  const actionGetRaffleRef = useRef(actionGetRaffle)
  actionGetRaffleRef.current = actionGetRaffle

  const countdownSecUntilClosed = state.raffle?.meta.open
    ? state.raffle?.meta.countdown_sec ?? -1
    : -1

  useEffect(() => { // poll raffle when countdown over
    let timeout: number
    if (countdownSecUntilClosed >= 0) {
      timeout = window.setTimeout(() => {
        actionGetRaffleRef.current?.()
      }, (countdownSecUntilClosed + 1) * 1000)
    }

    return () => {
      if (timeout) { window.clearTimeout(timeout) }
    }
  }, [countdownSecUntilClosed])

  const actionParticipate = useCallback(() => {
    setState((prev) => ({...prev, error: undefined}))
    forceLoginRef.current((user, token) => {
      setState((prev) => ({...prev, error: undefined, busy: true}))
      sendRef.current<ParticipateReq, ParticipateResp>("raffle/participate", { uid: uidRef.current, user_token: token })
        .then((resp) => {
          const respData = resp.data
          if (isApiError(respData)) {
            setState((prev) => ({...prev, busy: false, error: respData}))
          } else if (!respData || resp.error) {
            setState((prev) => ({...prev, busy: false, error: "couldNotParticipate" }))          
          } else {
            setState((prev) => {
              return {
                ...prev,
                busy: false,
                error: respData.cantParticipateReason ? { message: respData.cantParticipateReason } : undefined,
                raffle: respData.raffle,                
                wasAlreadyParticipatingOnFirstLoad: respData.wasAlreadyParticipating || undefined,
              }
            })
          }
        })
        .catch(() => {
          setState((prev) => ({...prev, busy: false, error: "couldNotParticipate" }))
        })
    })
  }, [])

  useEffect(() => { // load raffle on mount or uid change
    actionGetRaffleRef.current()
  }, [raffleUid])

  const context = useMemo<RaffleContextType>(() => {
    return {
      ...state,
      disableParticipateButton,
      actionGetRaffle,
      actionParticipate,
    }
  }, [state, actionGetRaffle, actionParticipate, disableParticipateButton])

  return <RaffleContext.Provider value={context}>{children}</RaffleContext.Provider>
}

export const WithConnectedRaffle: React.FC<PropsWithChildren<{raffleUid: RaffleUID}>> = (props) => {
  // remount on id change
  return <WithConnectedRaffleRaw key={props.raffleUid} {...props} />
}
